import type { AxiosInstance } from 'axios';
import type {
  U340xKpiLogicalUnitCode,
  U340xKpiMonthDetailTotals,
  U340xKpiStandaloneInitLU,
  U340xKpiStandaloneSeriesResult,
} from '../types/u340xKpiLuTypes';
import { u340xNormalizeDetailRows } from '../utils/u340xKpiLuConfig';
import type { U340xKpiMonthDetailResult } from '../types/u340xKpiLuTypes';

export const U340X_KPI_ZERO_TOTALS: U340xKpiMonthDetailTotals = {
  billableQuantity: 0,
  nonBillableQuantity: 0,
  billableCostAmount: 0,
  nonBillableCostAmount: 0,
  invoiceCount: 0,
  amountHt: 0,
  amountTec: 0,
  writeOffInvoiceCount: 0,
  writeOffAmountHt: 0,
  writeOffAmountTec: 0,
};

export function doFetchKpiInitLU(axios: AxiosInstance, code: U340xKpiLogicalUnitCode) {
  return axios.get(`${code}/initLU`);
}

export function doFetchStandaloneSeries(
  axios: AxiosInstance,
  code: U340xKpiLogicalUnitCode,
  months: number,
  anchorMonth: string,
) {
  return axios.get(`${code}/series`, {
    params: {
      mode: 'standalone',
      months,
      anchorMonth,
    },
  });
}

export function doFetchMonthDetail(
  axios: AxiosInstance,
  code: U340xKpiLogicalUnitCode,
  month: string,
) {
  return axios.get(`${code}/months/${encodeURIComponent(month)}/detail`);
}

export function parseStandaloneInitLU(raw: unknown): U340xKpiStandaloneInitLU {
  return raw as U340xKpiStandaloneInitLU;
}

export function parseStandaloneSeries(raw: unknown): U340xKpiStandaloneSeriesResult | null {
  return (raw as U340xKpiStandaloneSeriesResult) ?? null;
}

function toTotalNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function parseMonthDetailTotals(raw: unknown): U340xKpiMonthDetailTotals {
  if (!raw || typeof raw !== 'object') {
    return { ...U340X_KPI_ZERO_TOTALS };
  }
  const t = raw as Record<string, unknown>;
  return {
    billableQuantity: toTotalNumber(t.billableQuantity),
    nonBillableQuantity: toTotalNumber(t.nonBillableQuantity),
    billableCostAmount: toTotalNumber(t.billableCostAmount),
    nonBillableCostAmount: toTotalNumber(t.nonBillableCostAmount),
    invoiceCount: toTotalNumber(t.invoiceCount),
    amountHt: toTotalNumber(t.amountHt),
    amountTec: toTotalNumber(t.amountTec),
    writeOffInvoiceCount: toTotalNumber(t.writeOffInvoiceCount),
    writeOffAmountHt: toTotalNumber(t.writeOffAmountHt),
    writeOffAmountTec: toTotalNumber(t.writeOffAmountTec),
  };
}

export function parseMonthDetail(
  code: U340xKpiLogicalUnitCode,
  raw: unknown,
): U340xKpiMonthDetailResult | null {
  const result = raw as {
    month?: string;
    rows?: Array<Record<string, unknown>>;
    totals?: unknown;
  } | null;
  if (!result?.month) return null;
  const parsed: U340xKpiMonthDetailResult = {
    month: result.month,
    rows: u340xNormalizeDetailRows(code, result.rows ?? []),
  };
  // Totaux : U3402 (grain projet) et U3403 (standard / write-off)
  if (code === 'U3402' || code === 'U3403') {
    parsed.totals = parseMonthDetailTotals(result.totals);
  }
  return parsed;
}
